/**
 * API Client Configuration
 * Centralized HTTP client for all API calls
 */

// API Gateway base URL - used for auth and other gateway-routed services
// In development, Vite proxy handles /api/* requests
// In production, this will be the full URL
export const API_GATEWAY_URL = import.meta.env.VITE_API_GATEWAY_URL || ''

// API Gateway base URL - execution engine is accessed via /execution prefix in production
// For local development, set VITE_EXECUTION_ENGINE_URL=http://localhost:8085 (no /execution prefix needed)
const CAMPAIGN_BASE_URL = import.meta.env.VITE_CAMPAIGN_BASE_URL || ''
export const EXECUTION_API_BASE = import.meta.env.VITE_EXECUTION_ENGINE_URL || `${CAMPAIGN_BASE_URL}/execution`
export const API_V1 = `${EXECUTION_API_BASE}/api/v1`
export const DASHBOARD_API = `${EXECUTION_API_BASE}/api/dashboard`

// Admin Portal BFF API (for Finance & Monitoring dashboards)
// Local development: VITE_ADMIN_API_URL=http://localhost:8095 (direct connection, no /admin prefix)
// Production: VITE_ADMIN_API_URL=https://spark-api-gateway.fhpro.in (routes through /admin/** in gateway)
export const ADMIN_API_BASE = import.meta.env.VITE_ADMIN_API_URL || ''

export const ADMIN_API = `${ADMIN_API_BASE}/admin/api/v1`
export const FINANCE_API = `${ADMIN_API}/admin/finance`
export const MONITORING_API = `${ADMIN_API}/monitoring`

// Ingestion API (for Developer Console)
// Local development: VITE_INGESTION_API_URL=http://localhost:9083 (direct connection)
// Production: VITE_INGESTION_API_URL=https://spark-api-gateway.fhpro.in (via gateway with /admin prefix)
export const INGESTION_API_BASE = import.meta.env.VITE_INGESTION_API_URL || ''
const isIngestionProduction = INGESTION_API_BASE.includes('gateway.fhpro.in')
const ingestionPrefix = isIngestionProduction ? '/admin' : ''
export const INGESTION_API = `${INGESTION_API_BASE}${ingestionPrefix}/api/v1`


/**
 * Get auth headers from localStorage
 */
export const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem('accessToken')
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

/**
 * Fetch wrapper with error handling
 */
export async function fetchApi<T>(url: string, options?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options?.headers,
    },
  })

  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(`API Error ${response.status}: ${errorText}`)
  }

  return response.json()
}

/**
 * POST request helper
 */
export async function postApi<T>(url: string, data?: unknown): Promise<T> {
  return fetchApi<T>(url, {
    method: 'POST',
    body: data ? JSON.stringify(data) : undefined,
  })
}

/**
 * PUT request helper
 */
export async function putApi<T>(url: string, data: unknown): Promise<T> {
  return fetchApi<T>(url, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
}

/**
 * PATCH request helper
 */
export async function patchApi<T>(url: string, data: unknown): Promise<T> {
  return fetchApi<T>(url, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

/**
 * DELETE request helper
 */
export async function deleteApi<T>(url: string): Promise<T> {
  return fetchApi<T>(url, {
    method: 'DELETE',
  })
}

// Admin Portal API Key Management base URL
export const API_KEYS_API = `${ADMIN_API}/admin/api-keys`

// Admin Portal Accounts Management base URL
export const ACCOUNTS_API = `${ADMIN_API}/admin/accounts`

// Finance Consumables API
export const CONSUMABLES_API = `${FINANCE_API}/consumables`

// Consumable Balances API (Finance-role manual view/adjustment of per-account, per-channel
// credit balances — distinct from CONSUMABLES_API above, which is the read-only StarRocks
// event-count report at /finance/consumables)
export const CONSUMABLE_BALANCES_API = `${ADMIN_API}/admin/consumables/balances`

// Derivation Service API base URL
export const DERIVATION_API = `${ADMIN_API}/admin/derivation`

// Engineering Redis Console API (search/inspect/force-expire keys on the shared Redis instance)
export const REDIS_CONSOLE_API = `${ADMIN_API}/admin/redis`

// fh_admin service API — gateway strips leading /admin, backend sees /api/v1/...
export const FH_ADMIN_API = ADMIN_API

/**
 * Fetch helper that injects X-Account-ID header — required for all API key endpoints.
 */
export async function fetchWithAccount<T>(
  url: string,
  accountId: string,
  options?: RequestInit,
): Promise<T> {
  return fetchApi<T>(url, {
    ...options,
    headers: {
      'X-Account-ID': accountId,
      ...options?.headers,
    },
  })
}
